import { cookieKeys, getCookie } from 'utils';
import i18next from 'i18next';

import { EMPTY_OBJECT, HTTP_METHOD, HTTP_STATUS } from './constants';

interface CallApiOptions {
    body?: Record<string, unknown>;
    headers?: Record<string, string>;
    method?: string;
}

export const callApiWithResponse = async <T>(
    endpoint: string,
    options = EMPTY_OBJECT as CallApiOptions,
): Promise<{ data: T; response: Response } | Record<string, never>> => {
    const response = await fetch(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            credentials: 'same-origin',
            'X-XSRF-TOKEN': getCookie(cookieKeys.FX_XSRF),
            ...(options.headers || EMPTY_OBJECT),
        },
        method: options.method || HTTP_METHOD.GET,
        ...(options.body ? { body: JSON.stringify(options.body) } : EMPTY_OBJECT),
    });

    if (response.status >= HTTP_STATUS.INTERNAL_SERVER_ERROR) {
        throw new Error(`${i18next.t('Sorry, something went wrong, please try again later')}.`);
    }

    if (!response.ok) {
        throw await response.json();
    }

    if (response.headers.get('content-type')?.includes('application/json')) {
        const data = (await response.json()) as T;

        return { data, response };
    }

    return EMPTY_OBJECT;
};

export const callApi = async <T>(endpoint: string, options?: CallApiOptions): Promise<T> => {
    const result = await callApiWithResponse<T>(endpoint, options);
    return (result as { data: T }).data;
};

export const post = <T>(
    endpoint: string,
    body?: CallApiOptions['body'],
    headers?: CallApiOptions['headers'],
): Promise<T> => callApi<T>(endpoint, { body, method: HTTP_METHOD.POST, headers });
